import * as React from "react";
import type { StackState } from "./Stack.types";
import { makeStyles, mergeClasses } from "@fluentui/react-components";
import {
  useAlignmentParser,
  useGapParser,
  useLengthParser,
} from "../../hooks/useParsers";

export const acuiStackCssVars = {
  justifyContent: "--acui-AppStack__justifyContent",
  alignItems: "--acui-AppStack__alignItems",
  height: "--acui-AppStack__height",
  innerWidth: "--acui-AppStack__innerWidth",
  innerHeight: "--acui-AppStack__innerHeight",
  maxWidth: "--acui-AppStack__maxWidth",
  maxHeight: "--acui-AppStack__maxHeight",
  verticalMargin: "--acui-AppStack__verticalMargin",
  horizontalMargin: "--acui-AppStack__horizontalMargin",
  padding: "--acui-AppStack__innerPadding",
  childMargin: "--acui-AppStack__childMargin",
  childMaxWidth: "--acui-AppStack__childMaxWidth",
  childMaxHeight: "--acui-AppStack__childMaxHeight",
  childMarginTop: "--acui-AppStack__childMarginTop",
  childMarginLeft: "--acui-AppStack__childMarginLeft",
};

const useCommonStyles = makeStyles({
  flexDirectionRow: {
    flexDirection: "row",
  },
  flexDirectionColumn: {
    flexDirection: "column",
  },
  justifyContentStyle: {
    justifyContent: `var(${acuiStackCssVars.justifyContent})`,
  },
  alignItemsStyle: {
    alignItems: `var(${acuiStackCssVars.alignItems})`,
  },
});

const useWrapStyles = makeStyles({
  root: {
    display: "flex",
    flexWrap: "wrap",
    width: "auto",
    overflow: "visible",
    height: `var(${acuiStackCssVars.height})`,
  },
  inner: {
    display: "flex",
    flexWrap: "wrap",
    marginLeft: `var(${acuiStackCssVars.horizontalMargin})`,
    marginRight: `var(${acuiStackCssVars.horizontalMargin})`,
    marginTop: `var(${acuiStackCssVars.verticalMargin})`,
    marginBottom: `var(${acuiStackCssVars.verticalMargin})`,
    overflow: "visible",
    boxSizing: "border-box",
    padding: `var(${acuiStackCssVars.padding})`,
    width: `var(${acuiStackCssVars.innerWidth})`,
    height: `var(${acuiStackCssVars.innerHeight})`,
    maxWidth: "100vw",
  },
  childStyles: {
    "& > *": {
      textOverflow: "ellipsis",
      margin: `var(${acuiStackCssVars.childMargin})`,
    },
  },
  maxWidthStyle: {
    maxWidth: `var(${acuiStackCssVars.maxWidth})`,
  },
  maxHeightStyle: {
    maxHeight: `var(${acuiStackCssVars.maxHeight})`,
  },
  childMaxWidthStyle: {
    "& > *": {
      maxWidth: `var(${acuiStackCssVars.childMaxWidth})`,
    },
  },
  childMaxHeightStyle: {
    "& > *": {
      maxHeight: `var(${acuiStackCssVars.childMaxHeight})`,
    },
  },
});

const useNoWrapStyles = makeStyles({
  root: {
    display: "flex",
    flexWrap: "nowrap",
    width: "auto",
    height: `var(${acuiStackCssVars.height})`,
    padding: `var(${acuiStackCssVars.padding})`,
    boxSizing: "border-box",
  },
  childHorizontalStyle: {
    "& > *:not(:first-child)": {
      marginLeft: `var(${acuiStackCssVars.childMarginLeft})`,
    },
  },
  childVerticalStyle: {
    "& > *:not(:first-child)": {
      marginTop: `var(${acuiStackCssVars.childMarginTop})`,
    },
  },
});

export const useStackCssVars = (state: StackState) => {
  const { horizontal, tokens, horizontalAlign, verticalAlign, verticalFill } =
    state;

  const { rowGap, columnGap } = useGapParser(tokens?.childrenGap);
  const alignment = {
    horizontalAlign: useAlignmentParser(horizontalAlign),
    verticalAlign: useAlignmentParser(verticalAlign),
  };

  state.root.style = {
    [`${acuiStackCssVars.justifyContent}`]: horizontal
      ? alignment.horizontalAlign
      : alignment.verticalAlign,
    [`${acuiStackCssVars.alignItems}`]: horizontal
      ? alignment.verticalAlign
      : alignment.horizontalAlign,
    [`${acuiStackCssVars.height}`]:
      verticalFill || !horizontal ? "100%" : "auto",
    [`${acuiStackCssVars.horizontalMargin}`]: `${-0.5 * columnGap.value}${columnGap.unit}`,
    [`${acuiStackCssVars.verticalMargin}`]: `${-0.5 * rowGap.value}${rowGap.unit}`,
    [`${acuiStackCssVars.padding}`]: useLengthParser(tokens?.padding),
    [`${acuiStackCssVars.innerWidth}`]:
      columnGap.value === 0
        ? "100%"
        : `calc(100% + ${columnGap.value}${columnGap.unit})`,
    [`${acuiStackCssVars.innerHeight}`]:
      rowGap.value === 0
        ? "100%"
        : `calc(100% + ${rowGap.value}${rowGap.unit})`,
    [`${acuiStackCssVars.childMargin}`]: `${0.5 * rowGap.value}${rowGap.unit} ${0.5 * columnGap.value}${columnGap.unit}`,
    ...(horizontal
      ? {
          [`${acuiStackCssVars.childMaxWidth}`]:
            columnGap.value === 0
              ? "100%"
              : `calc(100% + ${columnGap.value}${columnGap.unit})`,
          [`${acuiStackCssVars.childMarginLeft}`]: `${columnGap.value}${columnGap.unit}`,
        }
      : {
          [`${acuiStackCssVars.childMaxHeight}`]:
            rowGap.value === 0
              ? "100%"
              : `calc(100% + ${rowGap.value}${rowGap.unit})`,
          [`${acuiStackCssVars.childMarginTop}`]: `${rowGap.value}${rowGap.unit}`,
        }),
    [`${acuiStackCssVars.maxWidth}`]: useLengthParser(tokens?.maxWidth),
    [`${acuiStackCssVars.maxHeight}`]: useLengthParser(tokens?.maxHeight),
    ...state.root.style,
  };
};

export const useStackStyles = (state: StackState) => {
  const {
    wrap,
    horizontal,
    tokens,
    className,
    horizontalAlign,
    verticalAlign,
  } = state;

  const wrapStyles = useWrapStyles();
  const noWrapStyles = useNoWrapStyles();
  const commonStyles = useCommonStyles();

  useStackCssVars(state);

  const { rowGap, columnGap } = useGapParser(tokens?.childrenGap);

  state.root.className = React.useMemo(
    () =>
      mergeClasses(
        wrap ? wrapStyles.root : noWrapStyles.root,
        !wrap && horizontal
          ? commonStyles.flexDirectionRow
          : commonStyles.flexDirectionColumn,
        !wrap &&
          horizontal &&
          columnGap.value > 0 &&
          noWrapStyles.childHorizontalStyle,
        !wrap &&
          !horizontal &&
          rowGap.value > 0 &&
          noWrapStyles.childVerticalStyle,
        /*
          if it is a horizontal stack and horizontalAlign is set,
          or if it is a vertical stack and verticalAlign is set then we apply justifyContent style
        */
        ((horizontal && horizontalAlign) || (!horizontal && verticalAlign)) &&
          commonStyles.justifyContentStyle,
        /*
          if it is a horizontal stack and verticalAlign is set,
          or if it is a vertical stack and horizontalAlign is set then we apply alignItems style
        */
        ((horizontal && verticalAlign) || (!horizontal && horizontalAlign)) &&
          commonStyles.alignItemsStyle,
        !!tokens?.maxWidth && wrapStyles.maxWidthStyle,
        !!tokens?.maxHeight && wrapStyles.maxHeightStyle,
        className,
        state.root.className
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      wrap,
      horizontal,
      columnGap.value,
      rowGap.value,
      horizontalAlign,
      verticalAlign,
      tokens?.maxWidth,
      tokens?.maxHeight,
      className,
      state.root.className,
    ]
  );

  state.inner.className = React.useMemo(
    () =>
      wrap
        ? mergeClasses(
            wrapStyles.inner,
            ((horizontal && horizontalAlign) ||
              (!horizontal && verticalAlign)) &&
              commonStyles.justifyContentStyle,
            ((horizontal && verticalAlign) ||
              (!horizontal && horizontalAlign)) &&
              commonStyles.alignItemsStyle,
            horizontal
              ? commonStyles.flexDirectionRow
              : commonStyles.flexDirectionColumn,
            horizontal
              ? wrapStyles.childMaxWidthStyle
              : wrapStyles.childMaxHeightStyle,
            wrapStyles.childStyles,
            state.inner && state.inner.className
          )
        : "",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [wrap, horizontal, horizontalAlign, verticalAlign, state.inner]
  );
};
